import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import type { ConsentPolicyConfiguration } from '../../types/domain';
import { loadGoogleIdentity } from './googleIdentity';
import './googleSignIn.css';
import { SignupPath } from './SignupPath';
import type { SignupAccountType } from './SignupPath';

interface Challenge { challenge_id: string; nonce: string; client_id: string }
type NextStep = { status: 'registration_required'; email: string; full_name: string }
  | { status: 'link_required'; email: string };
type GoogleResult = NextStep | { status: 'authenticated'; access: string; refresh: string; created: boolean; user: { is_brand_ambassador: boolean } };

export function GoogleBaSignIn({ initialAccountType = 'brand_ambassador' }: { initialAccountType?: SignupAccountType }) {
  const { completeGoogleSignIn } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<{ challenge_id: string; credential: string } | null>(null);
  const activeRef = useRef(true);
  const [enabled, setEnabled] = useState(false);
  const [accountType, setAccountType] = useState<SignupAccountType>(initialAccountType);
  const [retry, setRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<NextStep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [policies, setPolicies] = useState<ConsentPolicyConfiguration | null>(null);

  function acceptResult(result: GoogleResult) {
    if (result.status === 'authenticated') {
      requestRef.current = null;
      setPassword('');
      completeGoogleSignIn(result);
      void navigate(result.created && result.user?.is_brand_ambassador ? '/talent' : '/', { replace: true });
    } else {
      setStep(result);
      if (result.status === 'registration_required') setName(result.full_name);
    }
  }

  // Each rendered Google button gets a fresh backend nonce. Credentials live only in memory.
  useEffect(() => {
    let active = true;
    void apiRequest<{ enabled: boolean }>('/auth/google/config/')
      .then((config) => { if (active) setEnabled(config.enabled); })
      .catch(() => { /* Preserve password login if this API has not been deployed yet. */ });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    activeRef.current = true;
    void (async () => {
      try {
        const [challenge, google, configuration] = await Promise.all([
          apiRequest<Challenge>('/auth/google/challenge/', { method: 'POST', body: '{}' }),
          loadGoogleIdentity(),
          apiRequest<ConsentPolicyConfiguration>('/auth/consents/config/'),
        ]);
        if (!active) return;
        setPolicies(configuration);
        google.initialize({
          client_id: challenge.client_id, nonce: challenge.nonce, auto_select: false,
          callback: ({ credential }) => {
            if (!active) return;
            setBusy(true);
            setError(null);
            requestRef.current = { challenge_id: challenge.challenge_id, credential };
            void apiRequest<GoogleResult>('/auth/google/', {
              method: 'POST', body: JSON.stringify(requestRef.current),
            }).then((result) => { if (active) acceptResult(result); })
              .catch((cause: unknown) => { if (active) setError(getApiErrorMessage(cause)); })
              .finally(() => { if (active) setBusy(false); });
          },
        });
        if (buttonRef.current) google.renderButton(buttonRef.current, {
          theme: 'outline', size: 'large', text: 'continue_with', width: 280,
        });
      } catch (cause) {
        if (active) setError(getApiErrorMessage(cause));
      }
    })();
    return () => {
      active = false;
      activeRef.current = false;
      requestRef.current = null;
    };
    // Auth context changes during login; do not reinitialise an in-flight Google interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, retry]);

  async function finish(event: React.FormEvent) {
    event.preventDefault();
    if (!requestRef.current || !step || busy) return;
    if (step.status === 'registration_required' && (!policies || !terms || !privacy)) {
      setError('Accept the current Terms and Privacy Notice to create your BFC account.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await apiRequest<GoogleResult>('/auth/google/', {
        method: 'POST', body: JSON.stringify({
          ...requestRef.current,
          ...(step.status === 'link_required' ? { existing_password: password } : {
            registration: { account_type: accountType, full_name: name.trim(), ...(accountType === 'brand_ambassador' ? { city: city.trim() } : {}), terms_accepted: terms,
              privacy_accepted: privacy, marketing_consent: marketing,
              terms_version: policies!.terms.version, privacy_version: policies!.privacy.version,
              marketing_version: policies!.marketing.version },
          }),
        }),
      });
      if (activeRef.current) acceptResult(result);
    } catch (cause) {
      if (activeRef.current) setError(getApiErrorMessage(cause));
    } finally {
      if (activeRef.current) setBusy(false);
    }
  }

  function restart() {
    requestRef.current = null;
    setStep(null); setError(null); setPassword('');
    setTerms(false); setPrivacy(false); setMarketing(false);
    setRetry((value) => value + 1);
  }

  if (!enabled) return null;
  return <section className="google-ba-sign-in" aria-label="Google sign-in">
    <p className="muted">Continue with your Google account.</p>
    <div ref={buttonRef} hidden={Boolean(step) || busy} />
    {busy ? <p role="status">Connecting your BFC account…</p> : null}
    {step ? <form className="login-form" onSubmit={(event) => { void finish(event); }}>
      <h3>{step.status === 'link_required' ? 'Link your existing BFC account' : 'Finish your signup'}</h3>
      <p>{step.email}</p>
      {step.status === 'link_required' ? <>
        <p className="muted">Confirm your BFC password once to keep your existing profile, assignments and history.</p>
        <label>BFC password<input autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} required type="password" value={password} /></label>
        <Link to="/forgot-password">Forgot your BFC password?</Link>
      </> : <>
        <SignupPath value={accountType} onChange={setAccountType} disabled={busy} />
        <label>Full name<input autoComplete="name" maxLength={120} onChange={(e) => setName(e.target.value)} required value={name} /></label>
        {accountType === 'brand_ambassador' ? <label>City<input autoComplete="address-level2" maxLength={80} onChange={(e) => setCity(e.target.value)} required value={city} /></label> : null}
        <fieldset className="consent-fieldset" disabled={busy || !policies}>
          <legend>Before you continue</legend>
          <label className="consent-option"><input checked={terms} onChange={(e) => setTerms(e.target.checked)} type="checkbox" /><span>I accept the <a href={policies?.terms.url} target="_blank" rel="noreferrer">Terms of Service</a>.</span></label>
          <label className="consent-option"><input checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} type="checkbox" /><span>I accept the <a href={policies?.privacy.url} target="_blank" rel="noreferrer">Privacy Notice</a>.</span></label>
          <label className="consent-option"><input checked={marketing} onChange={(e) => setMarketing(e.target.checked)} type="checkbox" /><span>Send me optional BFC news and campaign opportunities.</span></label>
        </fieldset>
        <p className="muted">Google sign-in does not grant organisation access or BFC verification.</p>
      </>}
      <button className="button primary full" disabled={busy} type="submit">{step.status === 'link_required' ? 'Link Google and sign in' : accountType === 'brand_ambassador' ? 'Create BA account with Google' : 'Create organisation account with Google'}</button>
    </form> : null}
    {error ? <p className="form-error" role="alert">{error}</p> : null}
    {error || step ? <button className="button ghost" disabled={busy} onClick={restart} type="button">Start Google sign-in again</button> : null}
    <p className="muted">Or use your email and password below.</p>
  </section>;
}
